import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const AlumniDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState([]);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');

  useEffect(() => {
    fetchAlumni();
  }, []);

  useEffect(() => {
    if (id && alumni.length > 0) {
      const found = alumni.find(a => a.id === parseInt(id));
      if (found) {
        setSelectedAlumni(found);
      } else if (alumni.length > 0) {
        setSelectedAlumni(alumni[0]);
        navigate(`/alumni/${alumni[0].id}`, { replace: true });
      }
    } else if (alumni.length > 0 && !selectedAlumni) {
      setSelectedAlumni(alumni[0]);
      navigate(`/alumni/${alumni[0].id}`, { replace: true });
    }
  }, [alumni, id, navigate]);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni');
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAlumni = (alumnus) => {
    setSelectedAlumni(alumnus);
    navigate(`/alumni/${alumnus.id}`);
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Chairman': return 'bg-yellow-100 text-yellow-800';
      case 'Vice Chairman': return 'bg-orange-100 text-orange-800';
      case 'Secretary': return 'bg-blue-100 text-blue-800';
      case 'Treasurer': return 'bg-green-100 text-green-800';
      case 'Patron': return 'bg-purple-100 text-purple-800';
      case 'Advisor': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    return `https://graduates.katinetti.ac.ke${photoUrl}?t=${Date.now()}`;
  };

  const roles = ['all', ...new Set(alumni.map(a => a.role))];

  const filteredAlumni = alumni.filter(alumnus => {
    const matchesSearch = searchTerm === '' ||
      alumnus.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumnus.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (alumnus.department_name && alumnus.department_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = selectedRole === 'all' || alumnus.role === selectedRole;

    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FEFBEC', minHeight: '100vh' }}>
      {/* Hero Section - Left Aligned with Gold Bottom Border - Reduced Height */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl">👥</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">
                Katine TTI Alumni Association
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Meet our distinguished alumni who are making a difference
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {alumni.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Alumni Found</h3>
            <p className="text-gray-600">Check back later to meet our distinguished alumni.</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Sidebar - Alumni List */}
            <div className="lg:w-1/3 xl:w-1/4">
              <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4">
                <div className="bg-primary text-white p-4">
                  <h2 className="text-lg font-semibold">All Alumni</h2>
                  <p className="text-sm text-white/80">{filteredAlumni.length} members</p>
                </div>

                <div className="p-3 border-b">
                  <input
                    type="text"
                    placeholder="🔍 Search by name, role or department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>

                <div className="p-3 border-b overflow-x-auto">
                  <div className="flex gap-2 flex-wrap">
                    {roles.map(role => (
                      <button
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          selectedRole === role
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {role === 'all' ? 'All' : role}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
                  {filteredAlumni.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No alumni found matching your search.</div>
                  ) : (
                    filteredAlumni.map((alumnus) => (
                      <div
                        key={alumnus.id}
                        onClick={() => handleSelectAlumni(alumnus)}
                        className={`p-3 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                          selectedAlumni?.id === alumnus.id
                            ? 'bg-primary/5 border-l-4 border-l-primary'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                            {getImageUrl(alumnus.photo_url) ? (
                              <img
                                src={getImageUrl(alumnus.photo_url)}
                                alt={alumnus.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(alumnus.name)}&background=1e3a8a&color=fff&size=100`;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                                <span className="text-white text-sm font-bold">{alumnus.name?.charAt(0)}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 text-sm truncate">{alumnus.name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(alumnus.role)}`}>
                                {alumnus.role}
                              </span>
                              {alumnus.department_name && (
                                <span className="text-xs text-gray-500 truncate">{alumnus.department_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Selected Alumni Profile */}
            <div className="lg:w-2/3 xl:w-3/4">
              {selectedAlumni && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    {/* Image Section - Left side */}
                    <div className="md:w-1/3 bg-gradient-to-br from-primary/5 to-secondary/5">
                      <div className="sticky top-0">
                        {getImageUrl(selectedAlumni.photo_url) ? (
                          <img
                            src={getImageUrl(selectedAlumni.photo_url)}
                            alt={selectedAlumni.name}
                            className="w-full h-auto object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAlumni.name)}&background=1e3a8a&color=fff&size=400`;
                            }}
                          />
                        ) : (
                          <div className="w-full aspect-square bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                            <span className="text-white text-6xl font-bold">{selectedAlumni.name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details Section - Right side */}
                    <div className="md:w-2/3 p-6">
                      <div className="mb-4">
                        <h1 className="text-2xl font-bold text-gray-800">{selectedAlumni.name}</h1>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getRoleBadgeClass(selectedAlumni.role)}`}>
                          {selectedAlumni.role}
                        </span>
                      </div>

                      <div className="space-y-3 text-sm">
                        {selectedAlumni.department_name && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Department:</span>
                            <span className="text-gray-900 sm:w-2/3">{selectedAlumni.department_name}</span>
                          </div>
                        )}

                        {selectedAlumni.graduation_year && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Graduation Year:</span>
                            <span className="text-gray-900 sm:w-2/3">{selectedAlumni.graduation_year}</span>
                          </div>
                        )}

                        {selectedAlumni.employment_status && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Employment Status:</span>
                            <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employment_status}</span>
                          </div>
                        )}

                        {selectedAlumni.employer && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Employer:</span>
                            <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employer}</span>
                          </div>
                        )}

                        {selectedAlumni.email && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Email:</span>
                            <span className="text-gray-900 sm:w-2/3">
                              <a href={`mailto:${selectedAlumni.email}`} className="text-primary hover:underline">{selectedAlumni.email}</a>
                            </span>
                          </div>
                        )}

                        {selectedAlumni.phone && (
                          <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                            <span className="font-semibold text-gray-700 sm:w-1/3">Phone:</span>
                            <span className="text-gray-900 sm:w-2/3">
                              <a href={`tel:${selectedAlumni.phone}`} className="text-primary hover:underline">{selectedAlumni.phone}</a>
                            </span>
                          </div>
                        )}
                      </div>

                      {selectedAlumni.journey_description && (
                        <div className="mt-6 pt-4 border-t">
                          <h3 className="font-semibold text-gray-800 mb-2">My Journey</h3>
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                            {selectedAlumni.journey_description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniDetails;
